// FTUE-v2 anonymous, account-free reveal (capabilities `boat-reveal` +
// `boat-claim-entry`; design D3/D4/D5).
//
// The boat hands the browser a read-scoped reveal token in the URL fragment
// (`#rt=…`). This page runs the WHOLE reveal before any account:
//
//   name beat  → "what do you call her?" (fills the discovery wait, personalizes)
//   found      → "we found your gateways" from the token-scoped projection
//   account    → create-an-account OR log-in, EQUAL WEIGHT, at the END — the one
//                and only login, which claims the boat (POST /v1/boats with the
//                reveal token; the redeemable token is exchanged server-side and
//                never touches the browser).
//
// "Continue with Google" is a full-page OIDC round trip, so the reveal token +
// name are stashed in sessionStorage (lib/revealResume.ts) right before the
// navigation. RevealEntry restores them (`resume`), and the account step then
// auto-claims once the session probe lands — the user already said "keep it"
// by picking Google.
//
// On claim we hand off to the authenticated reveal (`/reveal?boatId=…`), which
// renders the post-claim experience (devices, alarms, live) from the snapshot
// re-associated onto the new boat.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api';
import { useAuth } from '../auth';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { clearRevealResume, saveRevealResume } from '../lib/revealResume';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useRevealSnapshot } from '../lib/useRevealSnapshot';

type Step = 'name' | 'found' | 'account' | 'confirm-adopt';

interface SuggestedBoat {
  boatId: string;
  displayName: string;
  basis: string;
}

type ClaimResponse =
  | { boatId: string; status: string }
  | { status: 'requires_confirmation'; suggested: SuggestedBoat };
type AccountMode = 'create' | 'login';
type SubmitState = 'idle' | 'submitting' | 'error';

export function AnonymousReveal({
  revealToken,
  initialName,
  resume = false,
}: {
  revealToken: string;
  // Restored from the sessionStorage stash after the Google OIDC round trip:
  // the name from the name beat, and `resume` to skip straight to the account
  // step and auto-claim once the session probe lands.
  initialName?: string;
  resume?: boolean;
}) {
  useDocumentTitle('Your boat - Neomaris');
  const auth = useAuth();
  const navigate = useNavigate();
  const snapshot = useRevealSnapshot(revealToken);
  const { config, loaded: configLoaded } = useClientConfig();

  const [step, setStep] = useState<Step>(resume ? 'account' : 'name');
  const [name, setName] = useState(initialName ?? '');
  const [accountMode, setAccountMode] = useState<AccountMode>('create');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submit, setSubmit] = useState<SubmitState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [suggested, setSuggested] = useState<SuggestedBoat | null>(null);

  // T2: strip the reveal token out of the address bar on first load so it does
  // not linger in history / get copy-pasted. It stays in component state.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const boatName = name.trim() === '' ? 'your boat' : name.trim();
  const turnstileSiteKey = config?.turnstileSiteKey ?? null;

  // Gate the claim until the boat's snapshot has actually landed in the cloud
  // — a claim fired while the read still 404s is doomed to a
  // `boat_not_connected` rejection, so don't let the user walk into it. A
  // transient poll error does NOT gate (the claim may still succeed); once
  // `ready`, the hook never regresses to `waiting`.
  const claimGated = snapshot.kind === 'waiting' || snapshot.kind === 'loading';

  // Bound the silent wait: after a couple of minutes with no snapshot, tell
  // the user what to check instead of spinning forever.
  const [longWait, setLongWait] = useState(false);
  useEffect(() => {
    if (!claimGated) {
      setLongWait(false);
      return;
    }
    const timer = setTimeout(() => setLongWait(true), 150_000);
    return () => clearTimeout(timer);
  }, [claimGated]);

  // One honest copy per claim-rejection cause (never-expire lifecycle): a
  // missing snapshot is retryable (boat offline / still connecting), a
  // consumed row means the boat is already claimed, and only a genuinely
  // TTL'd legacy row earns the old expiry copy.
  function applyClaimError(e2: { status?: number; body?: { error?: string; message?: string } }) {
    if (e2.body?.error === 'boat_not_connected') {
      setError("We can't see your boat yet — check that it's powered and online, then try again.");
    } else if (e2.body?.error === 'already_claimed') {
      clearRevealResume(); // terminal: the token was consumed by a real claim
      setError('This boat was already claimed — log in to see it.');
    } else if (e2.body?.error === 'reveal_expired') {
      clearRevealResume(); // terminal: a stashed copy of this token is equally dead
      setError('This reveal link expired. Restart the boat to begin again.');
    } else {
      setError(e2.body?.message ?? 'Something went wrong. Please try again.');
    }
  }

  // The single login: establish a session (create OR log in), then claim the
  // boat with the reveal token + name and hand off to the post-claim reveal.
  // The claim may come back `requires_confirmation` (FTUE-v2 D7: the resolver
  // recognized this as a boat the user already owns) — then we show the adopt
  // confirmation rather than navigating.
  async function postClaim(extra: Record<string, unknown>): Promise<void> {
    const res = await api<ClaimResponse>('/v1/boats', {
      method: 'POST',
      idempotencyKey: crypto.randomUUID(),
      body: { revealToken, name: name.trim() === '' ? undefined : name.trim(), ...extra },
    });
    if ('status' in res && res.status === 'requires_confirmation' && 'suggested' in res) {
      setSuggested(res.suggested);
      setStep('confirm-adopt');
      setSubmit('idle');
      return;
    }
    if ('boatId' in res) {
      clearRevealResume(); // terminal: the token is consumed, the stash is dead weight
      navigate(`/reveal?boatId=${encodeURIComponent(res.boatId)}`, { replace: true });
    }
  }

  async function claimAndContinue(): Promise<void> {
    await postClaim({});
  }

  // D7 adopt: the user confirmed this is their existing boat → re-key it.
  async function onAdopt(): Promise<void> {
    if (suggested === null) return;
    setSubmit('submitting');
    setError(null);
    try {
      await postClaim({ adoptBoatId: suggested.boatId });
    } catch (err) {
      setError((err as { body?: { message?: string } }).body?.message ?? 'Something went wrong.');
      setSubmit('error');
    }
  }

  // The user said it's a different boat → insert a new one.
  async function onClaimNew(): Promise<void> {
    setSubmit('submitting');
    setError(null);
    try {
      await postClaim({ forceNew: true });
    } catch (err) {
      setError((err as { body?: { message?: string } }).body?.message ?? 'Something went wrong.');
      setSubmit('error');
    }
  }

  async function onAccountSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (accountMode === 'create' && turnstileSiteKey !== null && turnstileToken === null) return;
    setSubmit('submitting');
    setError(null);
    try {
      if (accountMode === 'create') {
        const body: { email: string; password: string; 'cf-turnstile-response'?: string } = {
          email,
          password,
        };
        if (turnstileToken !== null) body['cf-turnstile-response'] = turnstileToken;
        await api('/v1/auth/signup', { method: 'POST', body });
      }
      // Both paths end in a login so we hold a session for the claim — the ONE
      // authentication of the whole FTUE.
      await auth.login(email, password, turnstileToken);
      await claimAndContinue();
    } catch (err) {
      const e2 = err as { status?: number; body?: { error?: string; message?: string } };
      if (e2.status === 409 && e2.body?.error === 'email_in_use') {
        setAccountMode('login');
        setError('That email already has an account — log in to keep your boat.');
      } else {
        applyClaimError(e2);
      }
      if (turnstileSiteKey !== null) {
        setTurnstileToken(null);
        setTurnstileNonce((n) => n + 1);
      }
      setSubmit('error');
    }
  }

  // A returning, already-signed-in visitor: one click to keep the boat (still a
  // single authentication — they're already in).
  async function onClaimAsCurrentUser(): Promise<void> {
    setSubmit('submitting');
    setError(null);
    try {
      await claimAndContinue();
    } catch (err) {
      const e2 = err as { status?: number; body?: { error?: string; message?: string } };
      if (e2.status === 401 || e2.body?.error === 'unauthenticated') {
        // The cached session was stale/expired (e.g. the cookie lapsed since the
        // mount-time /v1/me probe). Re-probe: the 401 flips auth status →
        // anonymous, so the account step falls back to the signup/login tabs
        // instead of stranding on a dead one-click "Keep" that only ever 401s.
        await auth.refresh();
        setError('Your session expired — log in again to keep your boat.');
        setSubmit('error');
        return;
      }
      applyClaimError(e2);
      setSubmit('error');
    }
  }

  // "Use a different account": the signed-in visitor wants to claim under
  // another account. There is no partial sign-out — logout ends the Neomaris
  // session in THIS browser (the button copy says so). Once it flips status →
  // anonymous, the account step falls back to the create/log-in tabs.
  async function onUseDifferentAccount(): Promise<void> {
    setError(null);
    setSubmit('idle');
    try {
      await auth.logout();
    } catch {
      // Best-effort: useSession.logout clears local session state even if the
      // network call fails, so the tabs still render. Nothing to surface.
    }
  }

  // Auto-claim after the Google OIDC return: the user already said "keep it"
  // by picking Google, so once the session probe lands, claim without another
  // click. At most once per mount (the ref — postClaim mints a fresh
  // idempotency key per call, so a re-run would be a REAL second claim).
  // Errors fall through to the existing interactive paths (adopt confirm,
  // expiry copy, retry via the Keep button). If the probe resolves anonymous
  // (cancelled at Google / session didn't stick), the normal tabs render and
  // the stash keeps a retry alive.
  const autoClaimed = useRef(false);
  useEffect(() => {
    if (!resume || step !== 'account' || autoClaimed.current) return;
    if (auth.status !== 'authenticated') return;
    // Hold the auto-claim while the boat's snapshot hasn't landed — firing it
    // gated would burn the single shot on a guaranteed `boat_not_connected`.
    // The effect re-runs when the poll flips to `ready` and claims then.
    if (claimGated) return;
    autoClaimed.current = true;
    void onClaimAsCurrentUser();
    // onClaimAsCurrentUser is re-created each render; the ref makes the
    // effect single-shot, so listing it would only churn the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, step, auth.status, claimGated]);

  // A dead token's stash must not resurrect the resume on the next /reveal
  // load — clear it as soon as the snapshot poll reports the token terminal
  // (invalid, or consumed by a completed claim).
  useEffect(() => {
    if (snapshot.kind === 'expired' || snapshot.kind === 'claimed') clearRevealResume();
  }, [snapshot.kind]);

  if (snapshot.kind === 'claimed') {
    return (
      <main className="auth-panel">
        <div className="auth-card auth-card-v2">
          <h2>This boat was already claimed</h2>
          <p className="auth-sub">
            Someone finished setting this boat up. If that was you, log in to see it.
          </p>
          <a className="btn btn-primary auth-submit" href="/login">
            Log in
          </a>
        </div>
      </main>
    );
  }

  if (snapshot.kind === 'expired') {
    return (
      <main className="auth-panel">
        <div className="auth-card auth-card-v2">
          <h2>This link isn&apos;t valid</h2>
          <p className="auth-sub">
            Open your boat&apos;s setup page (the address it showed after install) to get a working
            link — it always has one ready.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-panel">
      <div className="auth-card auth-card-v2 reveal-anon">
        {step === 'name' && (
          <>
            <h2>Welcome aboard</h2>
            <p className="auth-sub">
              Your boat is getting set up. While it looks around — what do you call her?
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setStep('found');
              }}
            >
              <div className="form-group">
                <label htmlFor="boat-name">Boat name</label>
                <input
                  id="boat-name"
                  className="form-input"
                  type="text"
                  value={name}
                  maxLength={120}
                  placeholder="e.g. Serenity"
                  autoFocus
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary auth-submit">
                Continue
              </button>
            </form>
          </>
        )}

        {step === 'found' && (
          <>
            <h2>
              {snapshot.kind === 'ready' && snapshot.projection.gatewayCount > 0
                ? `We found ${snapshot.projection.gatewayCount} ${
                    snapshot.projection.gatewayCount === 1 ? 'gateway' : 'gateways'
                  } aboard ${boatName}`
                : `Looking around ${boatName}…`}
            </h2>
            {/* The gateway COUNT is a teaser, but the gateways themselves are
                revealed on the (post-claim) Gateway tab — not spoiled here. */}
            {!(snapshot.kind === 'ready' && snapshot.projection.gatewayCount > 0) ? (
              <p className="auth-sub" aria-live="polite">
                Scanning the network for instruments and gateways…
              </p>
            ) : null}
            <p className="auth-sub">
              Create an account or log in to keep {boatName} and see what each one is.
            </p>
            {/* Advance to the single claim confirmation. This teaser is NOT a
                second "Keep" — the account step is the one and only claim
                confirmation, so this button just continues. */}
            <button
              type="button"
              className="btn btn-primary auth-submit"
              onClick={() => setStep('account')}
            >
              Continue
            </button>
          </>
        )}

        {step === 'account' && (
          <>
            <h2>Keep {boatName}</h2>
            {claimGated && (
              <div className="auth-banner" aria-live="polite">
                <span className="banner-text">
                  {longWait
                    ? "Still waiting for your boat to connect. Check that it's powered and " +
                      'online — its setup page always has a working link.'
                    : 'Waiting for your boat to connect…'}
                </span>
              </div>
            )}
            {resume && auth.status === 'unknown' ? (
              // OAuth-return resume, session probe still in flight: without
              // this hold the anonymous tabs would flash and then flip to the
              // one-click/auto claim once the probe lands.
              <p className="auth-sub" aria-live="polite">
                Signing you in…
              </p>
            ) : auth.isAuthenticated ? (
              <>
                <p className="auth-sub">
                  You&apos;re signed in as {auth.user?.email}. Keep {boatName} on this account?
                </p>
                {error && (
                  <div className="auth-banner auth-banner--error">
                    <span className="banner-text">{error}</span>
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary auth-submit"
                  disabled={submit === 'submitting' || claimGated}
                  onClick={() => void onClaimAsCurrentUser()}
                >
                  {submit === 'submitting' ? 'Keeping…' : `Keep ${boatName}`}
                </button>
                <button
                  type="button"
                  className="tb-btn tb-btn--gray tb-btn--block reveal-anon__secondary"
                  disabled={submit === 'submitting'}
                  onClick={() => void onUseDifferentAccount()}
                >
                  Use a different account
                </button>
                <p className="tb-footnote reveal-anon__note">
                  Signs you out of Neomaris in this browser.
                </p>
              </>
            ) : (
              <>
                <p className="auth-sub">Create an account or log in — your boat is waiting.</p>
                {/* Full-page OIDC round trip — stash the reveal token + name
                    (restored by RevealEntry) so the redirect can't lose them. */}
                <GoogleSignInButton
                  from="/reveal"
                  onClick={() => saveRevealResume({ revealToken, name })}
                />
                <div className="auth-divider" role="separator" aria-label="or">
                  <span>or</span>
                </div>
                <div className="tb-seg reveal-anon__seg" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={accountMode === 'create'}
                    className="tb-seg__opt"
                    onClick={() => {
                      setAccountMode('create');
                      setError(null);
                    }}
                  >
                    Create account
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={accountMode === 'login'}
                    className="tb-seg__opt"
                    onClick={() => {
                      setAccountMode('login');
                      setError(null);
                    }}
                  >
                    Log in
                  </button>
                </div>
                {error && (
                  <div className="auth-banner auth-banner--error">
                    <span className="banner-text">{error}</span>
                  </div>
                )}
                <form onSubmit={(e) => void onAccountSubmit(e)}>
                  <div className="form-group">
                    <label htmlFor="reveal-email">Email</label>
                    <input
                      id="reveal-email"
                      className="form-input"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reveal-password">Password</label>
                    <input
                      id="reveal-password"
                      className="form-input"
                      type="password"
                      autoComplete={accountMode === 'create' ? 'new-password' : 'current-password'}
                      required
                      minLength={accountMode === 'create' ? 8 : 1}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  {accountMode === 'create' && turnstileSiteKey !== null && (
                    <TurnstileWidget
                      key={turnstileNonce}
                      siteKey={turnstileSiteKey}
                      onToken={(t) => setTurnstileToken(t)}
                    />
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary auth-submit"
                    disabled={
                      submit === 'submitting' ||
                      claimGated ||
                      !configLoaded ||
                      (accountMode === 'create' &&
                        turnstileSiteKey !== null &&
                        turnstileToken === null)
                    }
                  >
                    {submit === 'submitting'
                      ? 'Keeping your boat…'
                      : accountMode === 'create'
                        ? `Create account & keep ${boatName}`
                        : `Log in & keep ${boatName}`}
                  </button>
                </form>
              </>
            )}
          </>
        )}

        {step === 'confirm-adopt' && suggested !== null && (
          <>
            <h2>Is this {suggested.displayName || 'your existing boat'}?</h2>
            <p className="auth-sub">
              {boatName} looks like a boat you already have
              {suggested.displayName ? ` — “${suggested.displayName}”` : ''}. We can re-link this
              install to it and keep all its history, instead of creating a duplicate.
            </p>
            {error && (
              <div className="auth-banner auth-banner--error">
                <span className="banner-text">{error}</span>
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary auth-submit"
              disabled={submit === 'submitting'}
              onClick={() => void onAdopt()}
            >
              {submit === 'submitting'
                ? 'Re-linking…'
                : `Yes, re-link to ${suggested.displayName || 'it'}`}
            </button>
            <button
              type="button"
              className="tb-btn tb-btn--gray tb-btn--block reveal-anon__secondary"
              disabled={submit === 'submitting'}
              onClick={() => void onClaimNew()}
            >
              No, this is a new boat
            </button>
          </>
        )}
      </div>
    </main>
  );
}
